(function () {
  'use strict';

  const ENDPOINT = '/search/suggest.json';
  const DEBOUNCE_MS = 220;
  const MIN_CHARS = 2;
  const LIMIT_PER_TYPE = 4;
  const FETCH_LIMIT = 10; // over-fetch so hiding staged products doesn't leave gaps
  const HIDDEN_TAG = 'staging';

  const STORAGE_KEY = 'oj_recent_searches';
  const RECENT_MAX = 5;
  const RECENT_MIN_CHARS = 2;

  // Returns false for products tagged HIDDEN_TAG. Handles tags as an array or
  // a comma-separated string; if tags are missing, the product passes through.
  function isVisible(p) {
    const raw = p && p.tags;
    const tags = Array.isArray(raw) ? raw : String(raw || '').split(',');
    return !tags.some((t) => String(t).trim().toLowerCase() === HIDDEN_TAG);
  }

  const drawer = document.getElementById('SearchDrawer');
  const container = document.querySelector('[data-predictive-search]');
  if (!drawer || !container) return;

  const input = drawer.querySelector('.search__input, [type="search"]');
  if (!input) return;

  const resultsEl = container.querySelector('[data-predictive-results]');
  const loadingEl = container.querySelector('[data-predictive-loading]');
  const viewAllEl = container.querySelector('[data-predictive-view-all]');
  const viewAllTerm = container.querySelector('[data-predictive-view-all-term]');

  const recommendedEl = container.querySelector('[data-predictive-recommended]');
  const recommendedHeadingEl = container.querySelector('[data-predictive-recommended-heading]');

  const recentGroupEl = container.querySelector('[data-predictive-recent]');
  const recentListEl = container.querySelector('[data-predictive-recent-list]');
  const clearRecentBtn = container.querySelector('[data-predictive-clear-recent]');

  if (!resultsEl) {
    console.warn('[predictive-search] no [data-predictive-results] in container — check snippets/predictive-search.liquid');
    return;
  }

  let abortController = null;
  let debounceTimer = null;
  let activeIndex = -1;
  let items = [];

  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-autocomplete', 'list');
  input.setAttribute('aria-expanded', 'false');
  input.setAttribute('aria-controls', container.id);
  input.setAttribute('autocomplete', 'off');

  input.addEventListener('input', onInput);
  input.addEventListener('keydown', onKeydown);
  input.addEventListener('focus', () => {
    const term = input.value.trim();
    if (term.length >= MIN_CHARS) {
      query(term);
    } else {
      showResting();
    }
  });

  document.addEventListener('click', (e) => {
    if (!container.contains(e.target) && e.target !== input) hide();
  });

  drawer.querySelectorAll('.js-drawer-close').forEach((btn) => {
    btn.addEventListener('click', hide);
  });

  // Open the merchandising panel as soon as the drawer opens, without waiting
  // for the user to type. The theme's Drawer class fires 'drawerOpen.SearchDrawer'
  // via jQuery after the drawer's own open animation completes.
  const $drawer = window.jQuery && window.jQuery(drawer);
  if ($drawer && $drawer.on) {
    $drawer.on('drawerOpen.SearchDrawer', () => {
      setTimeout(() => {
        const term = input.value.trim();
        if (term.length < MIN_CHARS) showResting();
      }, 50);
    });
  }

  if (clearRecentBtn) {
    clearRecentBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      clearRecent();
    });
  }

  // Force navigation on submit button click, bypassing any form-level submit
  // interception (Rebuy SmartSearch, theme.Search, etc). Capture phase so we
  // fire before other bubble-phase handlers get a chance to preventDefault.
  const submitBtn = drawer.querySelector('.search-bar__submit, .search__submit');
  if (submitBtn) {
    submitBtn.addEventListener('click', (e) => {
      const currentForm = input.form;
      const term = input.value.trim();
      if (!term) return;
      e.preventDefault();
      e.stopPropagation();
      saveRecent(term);
      const action = (currentForm && currentForm.action) || '/search';
      window.location.href = `${action}?q=${encodeURIComponent(term)}&options[prefix]=last`;
    }, true);
  }

  // Record search intent when the user commits to any result — product,
  // collection, article, view-all, trending pick, recent pick, or featured link.
  container.addEventListener('click', (e) => {
    const link = e.target.closest('a[role="option"], a[data-predictive-view-all]');
    if (!link) return;
    const explicitTerm = link.getAttribute('data-term');
    const currentTerm = input.value.trim();
    saveRecent(explicitTerm || currentTerm);
  });

  function onInput() {
    const term = input.value.trim();
    clearTimeout(debounceTimer);
    if (term.length < MIN_CHARS) {
      showResting();
      return;
    }
    hideResting();
    debounceTimer = setTimeout(() => query(term), DEBOUNCE_MS);
  }

  async function query(term) {
    // Abort any prior in-flight request. Wrap in try in case of edge cases
    // where .abort() itself throws (rare, but has happened in some polyfills).
    if (abortController) {
      try { abortController.abort(); } catch (e) { /* no-op */ }
    }
    abortController = new AbortController();
    const signal = abortController.signal;

    show();
    hideResting();
    if (loadingEl) loadingEl.hidden = false;

    const url = new URL(ENDPOINT, window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'product,collection,page,article');
    url.searchParams.set('resources[limit]', FETCH_LIMIT);
    url.searchParams.set('resources[options][unavailable_products]', 'last');

    try {
      const res = await fetch(url.toString(), {
        signal: signal,
        headers: { Accept: 'application/json' }
      });
      if (signal.aborted) return;
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await res.json();
      if (signal.aborted) return;
      render(term, (data.resources && data.resources.results) || {});
    } catch (err) {
      // All abort variants are intentional — swallow silently
      if (err.name === 'AbortError' || err.name === 'DOMException' || signal.aborted) return;
      console.error('[predictive-search]', err);
      hide();
    } finally {
      if (loadingEl) loadingEl.hidden = true;
    }
  }

  function render(term, results) {
    const products = (results.products || []).filter(isVisible).slice(0, LIMIT_PER_TYPE);
    const collections = (results.collections || []).slice(0, LIMIT_PER_TYPE);
    const pages = (results.pages || []).slice(0, LIMIT_PER_TYPE);
    const articles = (results.articles || []).slice(0, LIMIT_PER_TYPE);
    const total = products.length + collections.length + pages.length + articles.length;

    if (total === 0) {
      resultsEl.innerHTML = `
        <div class="predictive-search__empty">
          No matches for "${escapeHtml(term)}"
        </div>`;
      resultsEl.hidden = false;
      if (viewAllEl) viewAllEl.hidden = true;
      // Keep recommended visible on no-match — retitle it for context
      if (recommendedEl) {
        recommendedEl.hidden = false;
        if (recommendedHeadingEl) recommendedHeadingEl.textContent = 'You might like';
      }
      activeIndex = -1;
      rebuildItems();
      return;
    }

    // Live results present — hide recommended so it doesn't compete
    if (recommendedEl) recommendedEl.hidden = true;

    let html = '';
    if (products.length) {
      html += `<div class="predictive-search__product-grid">${products.map(productCard).join('')}</div>`;
    }
    if (collections.length) {
      html += group('Collections', '', collections.map((c) => simpleItem(c.url, c.title)).join(''));
    }
    if (articles.length || pages.length) {
      html += group('Articles & pages', '',
        [...articles, ...pages].map((x) => simpleItem(x.url, x.title)).join(''));
    }

    resultsEl.innerHTML = html;
    resultsEl.hidden = false;

    if (viewAllTerm) viewAllTerm.textContent = term;
    if (viewAllEl) {
      viewAllEl.href = `/search?q=${encodeURIComponent(term)}&options[prefix]=last`;
      viewAllEl.hidden = false;
    }

    hideResting();
    rebuildItems();
  }

  function group(heading, extraClass, innerHtml) {
    return `
      <div class="predictive-search__group ${extraClass}">
        <h2 class="predictive-search__heading">${heading}</h2>
        <ul class="predictive-search__list">${innerHtml}</ul>
      </div>`;
  }

  function simpleItem(url, title) {
    return `<li><a class="predictive-search__item" role="option" href="${escapeAttr(url)}">${escapeHtml(title)}</a></li>`;
  }

  function productCard(p) {
    const imageUrl = (p.featured_image && p.featured_image.url) || p.image || '';
    const img = imageUrl
      ? `<img class="ps-card__image" src="${escapeAttr(imageUrl)}" alt="" loading="lazy" width="200" height="200">`
      : '<span class="ps-card__image ps-card__image--placeholder" aria-hidden="true"></span>';

    const price = p.price || p.price_min || '';
    const compare = p.compare_at_price;
    let priceMarkup = '';
    if (compare && compare !== price) {
      priceMarkup = `
        <span class="ps-card__price-sale">${escapeHtml(price)}</span>
        <s class="ps-card__price-compare">${escapeHtml(compare)}</s>`;
    } else if (price) {
      priceMarkup = escapeHtml(price);
    }

    const soldOut = p.available === false
      ? '<span class="ps-card__badge ps-card__badge--sold-out">Sold out</span>'
      : '';

    return `
      <a class="ps-card" role="option" href="${escapeAttr(p.url)}" data-ps-card>
        <div class="ps-card__image-wrap">
          ${img}
          ${soldOut}
        </div>
        <div class="ps-card__body">
          <div class="ps-card__title">${escapeHtml(p.title)}</div>
          <div class="ps-card__price">${priceMarkup}</div>
        </div>
      </a>`;
  }

  function onKeydown(e) {
    if (e.key === 'Escape') {
      hide();
      input.blur();
      return;
    }
    if (e.key === 'Enter') {
      // If user arrowed to a suggestion, activate that link instead of submitting
      if (activeIndex >= 0 && items[activeIndex]) {
        e.preventDefault();
        items[activeIndex].click();
        return;
      }
      // No selection — force navigation. Direct window.location.href bypasses
      // any submit-event interception (Rebuy, theme.Search, etc).
      const term = input.value.trim();
      if (term.length > 0) {
        e.preventDefault();
        saveRecent(term);
        const action = (input.form && input.form.action) || '/search';
        window.location.href = `${action}?q=${encodeURIComponent(term)}&options[prefix]=last`;
      }
      return;
    }
    if (!items.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      activeIndex = (activeIndex + 1) % items.length;
      updateActive();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      activeIndex = activeIndex <= 0 ? items.length - 1 : activeIndex - 1;
      updateActive();
    }
  }

  function updateActive() {
    items.forEach((el, i) => {
      const active = i === activeIndex;
      el.setAttribute('aria-selected', active ? 'true' : 'false');
      el.classList.toggle('is-active', active);
      if (active) {
        el.scrollIntoView({ block: 'nearest' });
        input.setAttribute('aria-activedescendant', el.id);
      }
    });
  }

  function show() {
    container.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  }

  function hide() {
    container.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    activeIndex = -1;
    items = [];
  }

  // ---------- Recent + trending + resting state ----------

  function getRecent() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  function saveRecent(term) {
    const clean = String(term || '').trim();
    if (clean.length < RECENT_MIN_CHARS) return;
    try {
      const current = getRecent();
      const filtered = current.filter((t) => t.toLowerCase() !== clean.toLowerCase());
      filtered.unshift(clean);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered.slice(0, RECENT_MAX)));
    } catch (e) {
      /* storage full or blocked — silently skip */
    }
  }

  function clearRecent() {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
    renderRecent();
  }

  function renderRecent() {
    if (!recentGroupEl || !recentListEl) return;
    const recent = getRecent();
    if (!recent.length) {
      recentGroupEl.hidden = true;
      recentListEl.innerHTML = '';
      return;
    }
    recentGroupEl.hidden = false;
    recentListEl.innerHTML = recent.map((term) => {
      const url = `/search?q=${encodeURIComponent(term)}&options[prefix]=last`;
      return `<li>
        <a class="predictive-search__sidebar-link" role="option"
           href="${escapeAttr(url)}" data-predictive-recent-item
           data-term="${escapeAttr(term)}">
          ${escapeHtml(term)}
        </a>
      </li>`;
    }).join('');
  }

  function showResting() {
    renderRecent();

    const hasRecent = recentGroupEl && !recentGroupEl.hidden;
    const hasTrending = !!container.querySelector('[data-predictive-trending]');
    const hasFeatured = !!container.querySelector('.predictive-search__featured-link');
    const hasRecommended = !!recommendedEl;

    // Nothing to show at all → don't open the panel
    if (!hasRecent && !hasTrending && !hasFeatured && !hasRecommended) return;

    if (resultsEl) resultsEl.hidden = true;
    if (viewAllEl) viewAllEl.hidden = true;
    if (recommendedEl) {
      recommendedEl.hidden = false;
      if (recommendedHeadingEl) recommendedHeadingEl.textContent = 'Recommended Products';
    }
    show();
    rebuildItems();
  }

  function hideResting() {
    if (resultsEl) resultsEl.hidden = false;
  }

  function rebuildItems() {
    items = Array.from(container.querySelectorAll('[role="option"], [data-predictive-view-all]'))
      .filter((el) => !el.closest('[hidden]'));
    items.forEach((el, i) => { el.id = `predictive-result-${i}`; });
    activeIndex = -1;
  }

  // ---------- Escape helpers ----------

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }
  function escapeAttr(s) { return escapeHtml(s); }
})();